import React from "react";
import { fmtCurrency } from "@/lib/api";
import { chartColors } from "@/lib/chartTheme";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
  Legend,
  Cell,
} from "recharts";
import { Target, Clock, TrendingUp } from "lucide-react";

function Empty({ msg }) {
  return (
    <div className="h-[120px] flex items-center justify-center text-sm text-muted-foreground text-center px-4">
      {msg}
    </div>
  );
}

const STAGE_MARGIN = { left: 10, right: 20 };
const STAGE_RADIUS = [0, 4, 4, 0];
const BAR_RADIUS = [4, 4, 0, 0];

export default function DashboardCharts({ stages, aging, trends, theme }) {
  const c = chartColors(theme);
  const tooltipStyle = {
    backgroundColor: theme === "dark" ? "#0d1526" : "#ffffff",
    border: `1px solid ${c.grid}`,
    borderRadius: 8,
    color: theme === "dark" ? "#fff" : "#0f172a",
    fontSize: 12,
  };

  return (
    <>
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Pipeline by stage */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" /> Pipeline by Stage
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stages.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={stages} layout="vertical" margin={STAGE_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={c.grid} />
                  <XAxis
                    type="number"
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    fontSize={11}
                    stroke={c.axis}
                  />
                  <YAxis type="category" dataKey="label" width={110} fontSize={11} stroke={c.axis} />
                  <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={tooltipStyle} />
                  <Bar dataKey="value" fill={c.primary} radius={STAGE_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty msg="No open-deal stage data." />
            )}
          </CardContent>
        </Card>

        {/* Deal aging */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" /> Deal Aging (open deals)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {aging.buckets?.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={aging.buckets}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={c.grid} />
                  <XAxis dataKey="range" fontSize={11} stroke={c.axis} />
                  <YAxis fontSize={11} stroke={c.axis} />
                  <Tooltip
                    formatter={(v, n) => (n === "value" ? fmtCurrency(v) : v)}
                    contentStyle={tooltipStyle}
                  />
                  <Bar dataKey="count" radius={BAR_RADIUS}>
                    {aging.buckets.map((b) => (
                      <Cell key={b.range} fill={c.aging[b.range]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty msg="No aging data." />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" /> Revenue Trends (last 12 months)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trends.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.grid} />
                <XAxis dataKey="month" fontSize={11} stroke={c.axis} />
                <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} fontSize={11} stroke={c.axis} />
                <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={tooltipStyle} />
                <Legend />
                <Line type="monotone" dataKey="created_value" name="Created" stroke={c.created} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="won_value" name="Won" stroke={c.won} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Empty msg="Not enough dated deals to build trends." />
          )}
        </CardContent>
      </Card>
    </>
  );
}
